import { StopOutlined, UndoOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import {
  Alert,
  Button,
  Input,
  Modal,
  Switch,
  Table,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { FC, useRef, useState } from 'react';

import { useAdminUserController_updateCurrentUser } from '@api-client/generated/AdminUserController/updateCurrentUser';
import { Schemas } from '@api-client/generated/types';
import { Card, Select } from '@components';
import { CompaniesSelect } from '@entities';
import {
  CANNOT_BLOCK_SELF_ERROR,
  IDENTITY_PROVIDER_UNAVAILABLE,
  LAST_ADMIN_ERROR,
  useBlockMember,
  useMembers,
} from '@hooks-api';
import { captureException } from '@sentry/react';
import { getApiErrorMessage } from '@utils';

import * as S from './styled';

const roles = [
  { label: 'Admin', value: 'admin' },
  { label: 'Accountant', value: 'accountant' },
  { label: 'KYC', value: 'kyc' },
  { label: 'Support/Sales', value: 'support' },
];

const roleLabel = (value: string) =>
  roles.find((role) => role.value === value)?.label ?? value;

/** Undefined when the button is live — antd renders no tooltip bubble for it. */
const disabledReason = (member: Schemas.User): string | undefined =>
  member.canBlock
    ? undefined
    : 'This account can’t be blocked — it’s either yours, or the last admin with backoffice access';

const Settings: FC = () => {
  // Off by default: the screen answers "who has access", and a roster that accumulates everyone who
  // ever left stops answering it. Per-visit state, not a saved preference.
  const [showBlocked, setShowBlocked] = useState(false);

  const {
    data: users,
    isPending,
    isError,
    refetch,
  } = useMembers(showBlocked);
  const { setBlocked, isBlocking } = useBlockMember();
  const { mutate: updateUser } = useAdminUserController_updateCurrentUser();

  // Held in a ref rather than state: the textarea lives inside an imperative Modal.confirm, which
  // does not re-render with the page, so a state update per keystroke would buy nothing.
  const reason = useRef('');

  const handleLinkCompanies = <K extends 'adminCompanyIds' | 'adminRole'>(
    key: K,
    id: string,
    value: Schemas.AdminUpdateUserDto[K]
  ) => {
    updateUser(
      {
        parameter: { id },
        // One field at a time is what this PATCH is for, and what it accepts: both properties of
        // AdminUpdateUserDto carry `@IsOptional()` on the backend. They are typed as required only
        // because the `@ApiProperty()` decorators omit `required: false`, so the schema — and the
        // client generated from it — overstate the contract. That belongs in the backend DTO.
        // @ts-expect-error-next-line
        requestBody: { [key]: value },
      },
      {
        onSuccess: () => {
          // Refetch rather than patch a local copy. The server owns canBlock, which depends on a
          // count across the whole table, and the toggle means there are two cached lists — a local
          // edit would leave whichever one is not on screen stale.
          void refetch();
          message.success(`User was updated`);
        },
        onError: (error) => {
          // Without this the refusal was silent: the picker went on showing the value the server
          // had rejected, and nothing on screen said the change had not been saved.
          captureException(error);
          void refetch();
          message.error(getApiErrorMessage(error, 'Failed to update the user'));
        },
      }
    );
  };

  /**
   * Explain a refused block or unblock in the screen's own words.
   *
   * Shared by both actions because every code below can come back from either: they are raised by
   * the same guard on the way in and the same identity-provider call on the way out.
   */
  const reportBlockingFailure = (error: unknown, fallback: string) => {
    const code = getApiErrorMessage(error, '');

    // Both mean the snapshot this row was rendered from is out of date, so the roster is refetched
    // and the actual reason explained instead of a generic failure.
    if (code === LAST_ADMIN_ERROR) {
      void refetch();
      message.error(
        'This is the last admin with backoffice access and can’t be blocked'
      );
      return;
    }

    if (code === CANNOT_BLOCK_SELF_ERROR) {
      void refetch();
      message.error('You can’t block your own account');
      return;
    }

    // The identity provider is what actually ends or restores the session, so when it cannot be
    // reached nothing changed at all — say that, rather than showing the raw backend code.
    if (code === IDENTITY_PROVIDER_UNAVAILABLE) {
      message.error(
        'Single sign-on is unavailable right now, so nothing was changed. Please try again in a moment.'
      );
      return;
    }

    // Not one of the rules this screen knows how to explain, so it is worth reporting.
    captureException(error);
    message.error(getApiErrorMessage(error, fallback));
  };

  const confirmBlock = (member: Schemas.User) => {
    reason.current = '';

    Modal.confirm({
      title: `Block ${member.fullName}?`,
      content: (
        <>
          <p>
            They will be signed out and won’t be able to log in, here or through
            single sign-on.
          </p>
          <Input.TextArea
            rows={2}
            maxLength={500}
            placeholder="Reason (optional)"
            aria-label="Reason for blocking"
            onChange={(event) => (reason.current = event.target.value)}
          />
        </>
      ),
      okText: 'Block',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await setBlocked(member.id, true, reason.current);
          message.success(
            showBlocked
              ? 'User blocked'
              : 'User blocked. Turn on “Show blocked” to see them.'
          );
        } catch (error) {
          reportBlockingFailure(error, 'Failed to block the user');
        }
      },
    });
  };

  const confirmUnblock = (member: Schemas.User) => {
    Modal.confirm({
      title: `Unblock ${member.fullName}?`,
      content: 'They will be able to log in again.',
      okText: 'Unblock',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await setBlocked(member.id, false);
          message.success('User unblocked');
        } catch (error) {
          reportBlockingFailure(error, 'Failed to unblock the user');
        }
      },
    });
  };

  const blockedTooltip = (member: Schemas.User) => {
    const on = dayjs(member.blockedAt).format('D MMM YYYY');
    const why = member.blockedDetails?.reason;

    return why ? `Blocked ${on} — ${why}` : `Blocked ${on}`;
  };

  const columns: TableProps<Schemas.User>['columns'] = [
    {
      title: <S.ColumnTitle>Name</S.ColumnTitle>,
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <S.NameWrapper vertical>
          <S.Name>
            {text}
            {record.blockedAt && (
              <Tooltip title={blockedTooltip(record)}>
                <S.BlockedTag color="red" bordered={false}>
                  Blocked
                </S.BlockedTag>
              </Tooltip>
            )}
          </S.Name>
          <S.Email>{record.email}</S.Email>
        </S.NameWrapper>
      ),
    },
    {
      title: <S.ColumnTitle>Role</S.ColumnTitle>,
      dataIndex: 'roles',
      key: 'roles',
      // A blocked member's role is shown flat, not as a Select: there is nothing to grant an account
      // that cannot sign in, and it separates the two groups visually when both are on screen.
      render: (_, record) => (
        <S.RoleWrapper>
          {record.blockedAt ? (
            roleLabel(record.adminRole)
          ) : (
            <Select
              size="large"
              defaultValue={record.adminRole}
              options={roles}
              onChange={(value) =>
                handleLinkCompanies('adminRole', record.id, value)
              }
            />
          )}
        </S.RoleWrapper>
      ),
    },
    {
      title: <S.ColumnTitle>Companies</S.ColumnTitle>,
      dataIndex: 'companies',
      key: 'companies',
      render: (companies: Schemas.Company[], record) =>
        record.blockedAt ? (
          <span>{companies.map((company) => company.name).join(', ') || '—'}</span>
        ) : (
          <CompaniesSelect
            placeholder="Select companies"
            // Label objects, not ids, and cast because CompaniesSelect's prop says `string[]`. This
            // is what the screen has always passed — it typechecked before only because `companies`
            // arrived untyped. Passing ids instead would be the "correct" call and a regression:
            // CompaniesSelect builds its options from its own paginated query, so any company past
            // the first page would render as a bare uuid. Widening that prop to match what it
            // actually accepts belongs with CompaniesSelect, not here.
            value={
              companies.map((item) => ({
                label: item.name,
                value: item.id,
              })) as unknown as string[]
            }
            mode="multiple"
            onChange={(value) => {
              handleLinkCompanies(
                'adminCompanyIds',
                record.id,
                value as string[]
              );
            }}
          />
        ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, record) =>
        record.blockedAt ? (
          <Button
            type="text"
            icon={<UndoOutlined />}
            disabled={isBlocking}
            onClick={() => confirmUnblock(record)}
            aria-label={`Unblock ${record.fullName}`}
          />
        ) : (
          // Whether the button is live comes from the server's canBlock, never from arithmetic here:
          // it depends on who is asking and on a count over the whole table, not over these rows.
          <Tooltip title={disabledReason(record)}>
            <Button
              type="text"
              danger
              icon={<StopOutlined />}
              disabled={!record.canBlock || isBlocking}
              onClick={() => confirmBlock(record)}
              aria-label={`Block ${record.fullName}`}
            />
          </Tooltip>
        ),
    },
  ];

  return (
    <S.Wrapper gap={24} vertical>
      <S.Header align="center" justify="space-between">
        <S.Title>Members access</S.Title>
        <S.ToggleWrapper align="center" gap={8}>
          <span>Show blocked</span>
          <Switch
            checked={showBlocked}
            onChange={setShowBlocked}
            aria-label="Show blocked users"
          />
        </S.ToggleWrapper>
      </S.Header>
      <Card>
        {/*
          An empty table is a truthful answer to "nobody has access" and a lie about a roster that
          failed to load — and this screen is where an admin goes to check exactly that. So a failed
          load says so, and offers the only action that helps.
        */}
        {isError && (
          <Alert
            type="error"
            showIcon
            message="The member roster could not be loaded."
            // Table's own empty state sits directly below, and flush against it the two read as one
            // confused surface.
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        )}
        <Table
          columns={columns}
          dataSource={users ?? []}
          pagination={false}
          loading={isPending}
          rowKey={(record) => record.id}
        />
      </Card>
    </S.Wrapper>
  );
};

export default Settings;
